import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, AlertTriangle, MessageCircle, Mail, Smartphone, Building } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { formatApiError } from "../lib/api";
import SEO, { ORG_JSONLD, breadcrumb } from "../components/SEO";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = {
  standard: {
    label: "Estándar",
    sub: "Sin contrato Post-Subasta",
    price: 335,
    detail: "Recibes la radiografía completa y el acompañamiento el día de la subasta.",
  },
  bonificado: {
    label: "Bonificado",
    sub: "Con contrato Gestión Post-Subasta",
    price: 135,
    detail: "Precio bonificado por contratar también la Gestión Completa post-subasta.",
  },
};

export default function InformePremiumForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialAuction = params.get("subasta") || params.get("auction") || "";
  const initialPlan = params.get("plan") === "bonificado" ? "bonificado" : "standard";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    tax_id: "",
    company: "",
    billing_address: "",
    billing_city: "",
    billing_postal_code: "",
    billing_country: "España",
    plan: initialPlan,
    auction_id: initialAuction,
    additional_info: "",
    website: "",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { id, amount, plan }

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setPlan = (plan) => setForm((p) => ({ ...p, plan }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.full_name.trim().length < 2) { setError("Indica tu nombre completo o razón social."); return; }
    if (!consent) { setError("Debes aceptar la Política de Privacidad para enviar la solicitud."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/informe-premium/solicitar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail) || "Error al enviar la solicitud.");
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "No se pudo enviar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <SEO
        title="Solicitar Informe Premium · Facturación"
        description="Solicita el Informe Premium de cargas y deudas con tus datos de facturación. Estándar 335 € o Bonificado 135 € (con Gestión Post-Subasta). Pago por transferencia bancaria o Bizum."
        path="/informe-premium/solicitar"
        jsonLd={[ORG_JSONLD, breadcrumb([
          { name: "Inicio", path: "/" },
          { name: "Informe Premium", path: "/informe-premium" },
          { name: "Solicitar", path: "/informe-premium/solicitar" },
        ])]}
      />
      <SiteHeader />

      {/* Hero */}
      <section className="bg-[#2d1f1a] text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Link to="/informe-premium" className="text-stone-400 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold mb-4" data-testid="back-to-informe">
            <ArrowLeft className="w-4 h-4" /> Volver al servicio
          </Link>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Solicitar Informe Premium</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-3">
            Datos de facturación y plan.<br /><span className="text-[#E0651F]">Te enviamos las instrucciones de pago.</span>
          </h1>
          <p className="text-stone-300 text-sm sm:text-base leading-relaxed max-w-3xl">
            Rellena tus datos fiscales, elige el plan que mejor encaja contigo y te enviaremos por email los datos para abonar el informe por <strong className="text-white">transferencia bancaria o Bizum</strong>.
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 w-full">
        {done ? (
          <div className="bg-white border-l-4 border-emerald-500 border border-stone-200 p-8 sm:p-10 rounded-sm shadow-sm" data-testid="informe-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-4" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-emerald-700 mb-2">Solicitud enviada</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">¡Gracias! Revisa tu email.</h2>
            <p className="text-stone-700 leading-relaxed mb-5">
              Te hemos enviado un correo con las <strong>instrucciones de pago</strong> (Bizum + transferencia bancaria) y el concepto a indicar. En cuanto confirmemos el ingreso, empezamos con el informe (normalmente menos de 24 h hábiles).
            </p>
            <div className="bg-[#fff7ef] border border-[#E0651F]/30 rounded-sm p-5 mb-6">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Importe a abonar</div>
              <div className="font-display text-3xl font-black text-[#2d1f1a] mb-1">{Number(done.amount).toFixed(2)} €</div>
              <div className="text-xs text-stone-600">IVA incluido · Plan {done.plan === "bonificado" ? "Bonificado" : "Estándar"}</div>
              <div className="text-xs text-stone-700 mt-3">
                <strong>Concepto:</strong> <code className="bg-white px-1.5 py-0.5 rounded-sm">INFORME-{(done.id || "").slice(0, 8).toUpperCase()}</code>
              </div>
              <div className="text-xs text-stone-700 mt-2 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5 text-[#E0651F]" /> <strong>Bizum:</strong> +34 695 536 486
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={buildWaUrl(WA_MSG.premium)} target="_blank" rel="noopener noreferrer"
                onClick={(ev) => { ev.preventDefault(); openWhatsApp(WA_MSG.premium); }}
                className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="success-whatsapp">
                <MessageCircle className="w-4 h-4" /> Adjuntar justificante por WhatsApp
              </a>
              <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-[#E0651F] hover:underline px-2 py-3 text-xs tracking-[0.18em] uppercase font-bold">
                Volver al inicio <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
            <form onSubmit={onSubmit} className="lg:col-span-3 bg-white border border-stone-200 rounded-sm p-6 sm:p-8 space-y-5" data-testid="informe-form">
              {/* PLAN */}
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">1 · Elige el plan</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {Object.entries(PLANS).map(([key, p]) => {
                    const active = form.plan === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setPlan(key)}
                        data-testid={`plan-${key}`}
                        className={`text-left p-4 rounded-sm border-2 transition-colors ${active
                          ? "border-[#E0651F] bg-[#fff7ef]"
                          : "border-stone-200 bg-white hover:border-stone-400"
                          }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-[10px] tracking-[0.25em] uppercase font-bold ${active ? "text-[#E0651F]" : "text-stone-500"}`}>{p.label}</span>
                          {active && <CheckCircle2 className="w-4 h-4 text-[#E0651F]" />}
                        </div>
                        <div className="font-display text-3xl font-black text-[#2d1f1a]">{p.price} €</div>
                        <div className="text-[11px] text-stone-500 mt-0.5">IVA incl. · {p.sub}</div>
                        <p className="text-xs text-stone-600 mt-2 leading-snug">{p.detail}</p>
                      </button>
                    );
                  })}
                </div>
                {form.plan === "bonificado" && (
                  <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs px-3 py-2 rounded-sm leading-relaxed">
                    <strong>⚠️ Condición del precio bonificado:</strong> incluye el compromiso de contratar también la <Link to="/gestion-postsubasta" className="font-bold underline">Gestión Completa Post-Subasta</Link> en caso de resultar adjudicatario. Si decides no continuar, deberás abonar la diferencia (200 € + IVA) al desistir.
                  </div>
                )}
              </div>

              <div className="border-t border-stone-200" />

              {/* PERSONALES */}
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F]">2 · Tus datos</div>
              <Field label="Nombre y apellidos (o razón social) *">
                <input value={form.full_name} onChange={update("full_name")} required data-testid="input-full-name"
                  className="form-input" autoComplete="name" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email *">
                  <input type="email" value={form.email} onChange={update("email")} required data-testid="input-email"
                    className="form-input" autoComplete="email" />
                </Field>
                <Field label="Teléfono *">
                  <input value={form.phone} onChange={update("phone")} required data-testid="input-phone"
                    className="form-input" placeholder="+34 ..." autoComplete="tel" />
                </Field>
              </div>

              <div className="border-t border-stone-200" />

              {/* FACTURACION */}
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F]">3 · Datos de facturación</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="NIF / CIF *">
                  <input value={form.tax_id} onChange={update("tax_id")} required data-testid="input-tax-id"
                    className="form-input" placeholder="12345678A o B12345678" />
                </Field>
                <Field label="Empresa (opcional)">
                  <input value={form.company} onChange={update("company")} data-testid="input-company"
                    className="form-input" placeholder="Si facturas a una sociedad" />
                </Field>
              </div>
              <Field label="Dirección de facturación *">
                <input value={form.billing_address} onChange={update("billing_address")} required data-testid="input-address"
                  className="form-input" placeholder="Calle, número, piso..." autoComplete="street-address" />
              </Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Código postal *">
                  <input value={form.billing_postal_code} onChange={update("billing_postal_code")} required data-testid="input-postal-code"
                    className="form-input" autoComplete="postal-code" inputMode="numeric" />
                </Field>
                <Field label="Ciudad *">
                  <input value={form.billing_city} onChange={update("billing_city")} required data-testid="input-city"
                    className="form-input" autoComplete="address-level2" />
                </Field>
                <Field label="País">
                  <input value={form.billing_country} onChange={update("billing_country")} data-testid="input-country"
                    className="form-input" autoComplete="country-name" />
                </Field>
              </div>

              <div className="border-t border-stone-200" />

              {/* SUBASTA */}
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F]">4 · Subasta de referencia</div>
              <Field label="ID de la subasta BOE (opcional)">
                <input value={form.auction_id} onChange={update("auction_id")} data-testid="input-auction-id"
                  className="form-input font-mono text-sm" placeholder="SUB-JA-2026-..." />
              </Field>
              <Field label="Información adicional o caso (opcional)">
                <textarea value={form.additional_info} onChange={update("additional_info")} rows={4} data-testid="input-additional"
                  className="form-input resize-none"
                  placeholder="¿Hay algo concreto que debamos saber? Plazos, dudas, contexto..." />
              </Field>

              {/* Honeypot */}
              <div style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }} aria-hidden="true">
                <label>Sitio web<input tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} /></label>
              </div>

              <label className="flex items-start gap-3 text-xs text-stone-600 leading-relaxed cursor-pointer" data-testid="consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#E0651F]" data-testid="consent-check" required />
                <span>He leído y acepto la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#E0651F] font-bold hover:underline">Política de Privacidad</a> y las <a href="/condiciones-contratacion" target="_blank" rel="noopener noreferrer" className="text-[#E0651F] font-bold hover:underline">Condiciones de Contratación</a>. Autorizo el tratamiento de mis datos para gestionar la solicitud del informe conforme al RGPD y a la LOPDGDD.</span>
              </label>

              {error && (
                <div className="flex items-start gap-3 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-sm" data-testid="informe-error">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={loading} data-testid="informe-submit"
                className="bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</> : <>Solicitar informe · {PLANS[form.plan].price} € <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <aside className="lg:col-span-2 space-y-5">
              <div className="bg-[#2d1f1a] text-white p-6 rounded-sm border-l-4 border-[#E0651F] sticky top-24">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Resumen</div>
                <div className="font-display text-xl font-black tracking-tight mb-3">{PLANS[form.plan].label} · {PLANS[form.plan].price} €</div>
                <div className="text-xs text-stone-300 leading-relaxed mb-5">{PLANS[form.plan].detail}</div>
                <div className="border-t border-stone-700 pt-4 space-y-2 text-xs">
                  <div className="text-stone-400 uppercase tracking-wide font-bold text-[9px]">Métodos de pago</div>
                  <div className="flex items-center gap-2"><Smartphone className="w-3.5 h-3.5 text-[#E0651F]" /> Bizum · +34 695 536 486</div>
                  <div className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-[#E0651F]" /> Transferencia bancaria</div>
                </div>
                <div className="border-t border-stone-700 mt-4 pt-4 space-y-2 text-xs">
                  <div className="text-stone-400 uppercase tracking-wide font-bold text-[9px]">Otras vías</div>
                  <a href={buildWaUrl(WA_MSG.premium)} target="_blank" rel="noopener noreferrer"
                    onClick={(ev) => { ev.preventDefault(); openWhatsApp(WA_MSG.premium); }}
                    className="flex items-center gap-2 text-stone-200 hover:text-white" data-testid="aside-whatsapp">
                    <MessageCircle className="w-3.5 h-3.5 text-[#E0651F]" /> WhatsApp
                  </a>
                  <a href="mailto:fmsubastasinvestment@gmail.com" className="flex items-center gap-2 text-stone-200 hover:text-white">
                    <Mail className="w-3.5 h-3.5 text-[#E0651F]" /> Email
                  </a>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>

      <SiteFooter />
      <style>{`
        .form-input { width:100%; padding:0.65rem 0.85rem; font-size:0.9rem; background:#fff; border:1px solid #d6d3d1; border-radius:2px; outline:none; transition:border-color .15s, box-shadow .15s; }
        .form-input:focus { border-color:#E0651F; box-shadow:0 0 0 2px rgba(224,101,31,0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
